// src/components/PublicDoorloopjeCard.js
// Publieke weergave van één doorloopje op een onderwerp-pagina:
// intro + stappen in een rij (uitklapbare tekst) + download als PDF. De titel linkt naar de
// publieke voorvertoning (voorvertoning: het pad) als die er is.
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { trackEvent } from '../utils/track';
import { bewaarDoorloopjePdf } from '../utils/doorloopjePdf';
import { ReactComponent as DownloadIcon } from '../assets/icon-download.svg';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

// Zelfde media-proxy als DoorloopjeDetail (CORS bij PDF-generatie)
const proxify = (url) => `${STRAPI_URL}/api/media-proxy?url=${encodeURIComponent(url)}`;

function PublicDoorloopjeCard({ doorloopje, logoUrl, voorvertoning }) {
  const [expanded, setExpanded] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const pdfContentRef = useRef(null);

  const steps = doorloopje.steps || [];

  const generatePDF = async () => {
    if (!pdfContentRef.current || isGeneratingPDF) return;
    setIsGeneratingPDF(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      await bewaarDoorloopjePdf(pdfContentRef.current, `Doorloopje - ${doorloopje.title.replace(/[^a-z0-9]/gi, '_')}.pdf`);
      trackEvent({
        type: 'pdf_download',
        path: window.location.pathname + window.location.search,
        title: document.title,
        payload: {
          doorloopje_id: doorloopje.id,
          doorloopje_title: doorloopje.title,
          public_page: true,
        },
      });
    } catch (err) {
      console.error('Fout bij PDF-generatie:', err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="odl-card">
      <div className="odl-header">
        <h2>{voorvertoning ? <Link to={voorvertoning}>{doorloopje.title}</Link> : doorloopje.title}</h2>
        <button
          type="button"
          className="btn-secondary odl-download-btn"
          onClick={generatePDF}
          disabled={isGeneratingPDF}
        >
          <DownloadIcon />
          {isGeneratingPDF ? 'Bezig…' : 'Download'}
        </button>
      </div>

      {doorloopje.description && (
        <div className={`odl-intro ${expanded ? '' : 'odl-clamped'}`}>
          <BlocksRenderer content={doorloopje.description} />
        </div>
      )}

      {steps.length > 0 && (
        <div className="odl-steps">
          {steps.map((step, index) => (
            <div key={step.id || index} className="odl-step">
              {step.image?.url && (
                <div className="odl-step-image">
                  <img src={step.image.url} alt={step.title || `Stap ${index + 1}`} />
                </div>
              )}
              <div className="odl-step-header">
                <span className="odl-step-number">{index + 1}</span>
                {step.title && <h3>{step.title}</h3>}
              </div>
              {step.description && (
                <div className={`odl-step-text ${expanded ? '' : 'odl-clamped'}`}>
                  <BlocksRenderer content={step.description} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button type="button" className="odl-expand-btn" onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Doorloopje inklappen ▲' : 'Volledige Doorloopje lezen ▼'}
      </button>

      {/* Verborgen PDF-container (zelfde opmaak als in de app) */}
      <div
        className={`pdf-export-container ${isGeneratingPDF ? 'generating' : ''}`}
        ref={pdfContentRef}
      >
        <div className="pdf-header">
          <div className="pdf-title-block">
            <p className="pdf-theme">{doorloopje.theme?.title?.toUpperCase() || 'ALGEMEEN'}</p>
            <h1 className="pdf-title">{doorloopje.title}</h1>
            {doorloopje.book && (
              <p className="pdf-book-info">
                Boek {doorloopje.book}
                {doorloopje.page ? `, Pagina ${doorloopje.page}` : ''}
              </p>
            )}
          </div>
          {logoUrl && (
            <img src={proxify(logoUrl)} alt="Doorloopjes" className="pdf-logo" crossOrigin="anonymous" />
          )}
        </div>

        {doorloopje.description && (
          <div className="pdf-description">
            <BlocksRenderer content={doorloopje.description} />
          </div>
        )}

        <div className="pdf-steps-wrapper">
          <div className="pdf-steps">
            {steps.map((step, index) => (
              <div key={`pdf-${step.id || index}`} className="pdf-step">
                {step.image?.url && (
                  <div className="pdf-step-image-container">
                    <img
                      src={proxify(step.image.url)}
                      alt={`Stap ${index + 1}`}
                      className="pdf-step-image"
                      crossOrigin="anonymous"
                    />
                  </div>
                )}
                <div className="pdf-step-content">
                  <div className="pdf-step-header">
                    <span className="pdf-step-number">{index + 1}</span>
                    {step.title && <h3 className="pdf-step-title">{step.title}</h3>}
                  </div>
                  {step.description && (
                    <div className="pdf-step-description">
                      <BlocksRenderer content={step.description} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pdf-footer">
          <p>www.doorloopjes.nl</p>
        </div>
      </div>
    </div>
  );
}

export default PublicDoorloopjeCard;
